import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { getSiteUrl } from '../../lib/siteConfig';

export default function QueueThankYou() {
  const { businessSlug } = useParams<{ businessSlug: string }>();
  const [liveStatusUrl, setLiveStatusUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [headerEnabled, setHeaderEnabled] = useState(false);
  const [headerColor, setHeaderColor] = useState('#111827');
  const [displayLogo, setDisplayLogo] = useState(false);
  const [displayCompanyName, setDisplayCompanyName] = useState(false);
  const [business, setBusiness] = useState<any>(null);
  const [thankYouTitle, setThankYouTitle] = useState("You're in the Queue!");
  const [thankYouMessage, setThankYouMessage] = useState("We've added you to the queue and will get to you as soon as possible.");

  useEffect(() => {
    const loadData = async () => {
      const params = new URLSearchParams(window.location.search);
      const bookingId = params.get('booking');
      let token = params.get('token');

      if (!bookingId) {
        window.location.href = `${getSiteUrl()}/${businessSlug}/que`;
        return;
      }

      // If coming from payment (no token yet), fetch it
      if (!token) {
        try {
          // Wait a moment for queue entry to be created
          await new Promise(resolve => setTimeout(resolve, 3000));
          
          // Fetch live link token
          const { data: linkData } = await supabase
            .from('queue_public_links')
            .select('token')
            .eq('booking_id', bookingId)
            .maybeSingle();
          
          if (linkData) {
            token = linkData.token;
          }
        } catch (err) {
          console.error('Error fetching queue data:', err);
        }
      }

      const seenKey = `que_thank_you_seen_${businessSlug}_${bookingId || 'unknown'}`;
      if (performance.getEntriesByType('navigation')[0]?.type === 'reload') {
        if (sessionStorage.getItem(seenKey)) {
          window.location.href = `${getSiteUrl()}/${businessSlug}/que`;
          return;
        }
        sessionStorage.setItem(seenKey, 'true');
      } else {
        sessionStorage.removeItem(seenKey);
      }
      
      if (token) {
        const liveUrl = `${getSiteUrl()}/${businessSlug}/que/status/${token}`;
        setLiveStatusUrl(liveUrl);
      }

      // Fetch business and design settings
      try {
        const { data: businessData, error: businessError } = await supabase
          .from('businesses')
          .select('id, name, booking_settings')
          .eq('slug', businessSlug)
          .single();

        console.log('Business data:', businessData, 'Error:', businessError);

        if (businessData) {
          setBusiness(businessData);

          const { data: settings, error: settingsError } = await supabase
            .from('que_settings')
            .select('header_enabled, header_color, display_logo, display_company_name, thank_you_title, thank_you_message')
            .eq('business_id', businessData.id)
            .maybeSingle();

          console.log('Queue settings:', settings, 'Error:', settingsError);

          if (settings) {
            console.log('Setting header values:', {
              headerEnabled: settings.header_enabled,
              headerColor: settings.header_color,
              displayLogo: settings.display_logo,
              displayCompanyName: settings.display_company_name
            });
            setHeaderEnabled(settings.header_enabled || false);
            setHeaderColor(settings.header_color || '#111827');
            setDisplayLogo(settings.display_logo || false);
            setDisplayCompanyName(settings.display_company_name || false);
            setThankYouTitle(settings.thank_you_title || "You're in the Queue!");
            setThankYouMessage(settings.thank_you_message || "We've added you to the queue and will get to you as soon as possible.");
          }
        }
      } catch (err) {
        console.error('Error loading design settings:', err);
      }
      
      setIsLoading(false);
    };

    loadData();
  }, [businessSlug]);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {headerEnabled && (
        <div
          className="h-24 w-full flex items-center justify-center px-6 sm:px-8 relative z-10"
          style={{
            backgroundColor: headerColor,
          }}
        >
          <div className="flex items-center gap-4">
            {displayLogo && business?.booking_settings?.logo_url && (
              <img
                src={business.booking_settings.logo_url}
                alt="Business logo"
                className="h-16 w-16 object-contain"
              />
            )}
            {displayCompanyName && business?.name && (
              <h1 className="text-xl sm:text-2xl font-bold text-white">
                {business.name}
              </h1>
            )}
          </div>
        </div>
      )}
      
      <div className="flex-1 flex items-center justify-center px-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
          {/* Success Icon */}
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          
          <h1 className="text-2xl font-semibold text-gray-900">{thankYouTitle}</h1>
          <p className="text-sm text-gray-600 mt-2">
            {thankYouMessage}
          </p>

          {liveStatusUrl && (
            <div className="mt-8">
              <div className="rounded-xl border-2 border-blue-200 bg-blue-50 p-6">
                <div className="text-lg font-semibold text-blue-900 mb-2">
                  Want to track your position?
                </div>
                <div className="text-sm text-blue-700 mb-5">
                  Click the button below to see your live queue position and status updates in real-time.
              </div>
              <a
                href={liveStatusUrl}
                  className="block w-full text-center py-4 px-4 border-0 rounded-xl shadow-md text-base font-bold text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors active:bg-blue-800"
              >
                  View My Live Queue Position
              </a>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

